/**
 * Platform fee terms acceptance (`glamora.api.terms.*`).
 *
 * Owner-only endpoints for accepting the platform fee terms via OTP signing.
 * All endpoints need a login; `company` defaults to the caller's salon.
 */

import { rpc, rpcGet, verified } from './api'
import type {
  CurrentTerms,
  TermsAcceptPayload,
  TermsAcceptResult,
  TermsOtpTiming,
  TermsStatus,
} from './types/api'

const TERMS_STATUS = 'glamora.api.terms.get_terms_status'
const TERMS_CURRENT = 'glamora.api.terms.get_current_terms'
const TERMS_REQUEST_OTP = 'glamora.api.terms.request_otp'
const TERMS_ACCEPT = 'glamora.api.terms.accept'

/**
 * Fetch the terms status for the current user's salon (or a specific company).
 * Roles: Owner, Manager, Receptionist, Staff. Customers get `not_applicable`.
 */
export async function fetchTermsStatus(company?: string): Promise<TermsStatus> {
  return verified<TermsStatus>(
    rpcGet(TERMS_STATUS, company ? { company } : {}),
    'the terms status',
    'state',
  )
}

/**
 * Fetch the current published terms and fee parameters.
 * Roles: Owner, Manager.
 */
export async function fetchCurrentTerms(
  company?: string,
): Promise<CurrentTerms> {
  return verified<CurrentTerms>(
    rpcGet(TERMS_CURRENT, company ? { company } : {}),
    'the current terms',
    'terms',
  )
}

/**
 * Request an OTP for terms acceptance (Owner only).
 * Returns timing info: `expires_in`, `expires_at`, `resend_after`.
 * In dev/test the response also includes `otp`.
 */
export async function requestTermsOtp(
  company?: string,
): Promise<TermsOtpTiming> {
  return verified<TermsOtpTiming>(
    rpc(TERMS_REQUEST_OTP, company ? { company } : {}),
    'the confirmation code',
    'resend_after',
  )
}

/**
 * Accept the current terms with OTP and typed-name signature (Owner only).
 * `payload` fields must be exactly: `version`, `otp`, `typed_name`, `agree`, `company?`.
 */
export async function acceptTerms(
  payload: TermsAcceptPayload,
): Promise<TermsAcceptResult> {
  return verified<TermsAcceptResult>(
    rpc(TERMS_ACCEPT, payload as unknown as Record<string, unknown>),
    'the terms acceptance',
    'accepted',
  )
}
